import { useContext, useMemo } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { useModal } from 'components/modals'

import { Box, Typography } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import { AssetInnerTable, AccountRow, CallToActionRow } from 'atoms/AssetsTable'
import { selectData } from 'layouts/onboarding'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

export default function LifeInsuranceAssets({ demoMode = false }) {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const { openModal } = useModal()

  const getProviderLogo = (providerValue) => {
    const provider = selectData.privateLifeInsuranceProviders.find(
      (p) => p.value === providerValue
    )
    return provider?.logo || ''
  }

  const getCoverageTypeLabel = (typeValue) => {
    const type = selectData.privateLifeInsuranceTypes.find(
      (t) => t.value === typeValue
    )
    return type?.label || ''
  }

  const products = (intakeState?.privateLifeInsurance || []).map((p) => ({
    ...p,
    id: p.id || uuidv4(),
  }))

  const totalCoverage = products.reduce(
    (sum, p) => sum + (Number(p.coverage) || 0),
    0
  )

  const rows = useMemo(() => {
    if (!products || products.length === 0) {
      return [
        <CallToActionRow
          key="noProducts"
          text="Add your first Private Life Insurance"
          onClick={() =>
            !demoMode &&
            openModal('addLifeInsurance', {
              products,
              updateIntakeStateAndSubmit,
              updateIntakeStateAtKey,
            })
          }
        />,
      ]
    }

    return products.map((product) => (
      <AccountRow
        key={product.id}
        name={`${
          selectData.privateLifeInsuranceProviders.find(
            (p) => p.value === product.provider
          )?.label || 'Private Life Insurance'
        }${
          getCoverageTypeLabel(product.type)
            ? ` - ${getCoverageTypeLabel(product.type)}`
            : ''
        }`}
        amount={product.coverage || 0}
        subType={product.type}
        img={getProviderLogo(product.provider)}
        onClick={() =>
          openModal('addLifeInsurance', {
            product,
            products,
            updateIntakeStateAndSubmit,
            updateIntakeStateAtKey,
          })
        }
      />
    ))
  }, [
    products,
    demoMode,
    openModal,
    updateIntakeStateAndSubmit,
    updateIntakeStateAtKey,
  ])

  return (
    <>
      {products.length > 0 && (
        <Box
          mt={2}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1Medium" color="#262627">
            Total Coverage:
          </Typography>
          <Typography variant="body1Medium" mr={5} color="#262627">
            {dollarFormat.format(totalCoverage)}
          </Typography>
        </Box>
      )}

      <AssetInnerTable
        rows={rows}
        addAccountRow="Add Private Life Insurance"
        openModal={openModal}
        openToPage="Private Life Insurance"
        onAddClick={() =>
          !demoMode &&
          openModal('addLifeInsurance', {
            products,
            updateIntakeStateAndSubmit,
            updateIntakeStateAtKey,
          })
        }
      />
    </>
  )
}
